import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { IndianRupee, TrendingUp, Package, Users, Clock, MapPin, ArrowUpRight } from 'lucide-react';
import { StatCard, PageHeader, Card, SectionTitle, StatusBadge } from '../../components/common/UIComponents';
import { dashboardAPI, ordersAPI } from '../../services/api';
import { DashboardStats, Order } from '../../types';

const topCities = [
  { city:'Mumbai', orders:18, revenue:1842000, pct:100 },
  { city:'Delhi', orders:14, revenue:1240000, pct:67 },
  { city:'Bangalore', orders:9, revenue:980000, pct:53 },
  { city:'Hyderabad', orders:6, revenue:524000, pct:28 },
  { city:'Pune', orders:5, revenue:410000, pct:22 },
];

const monthlyData = [
  { m:'Jan', r:3.2, c:0.32 },{ m:'Feb', r:3.8, c:0.38 },{ m:'Mar', r:4.5, c:0.45 },
  { m:'Apr', r:4.2, c:0.42 },{ m:'May', r:5.1, c:0.51 },{ m:'Jun', r:5.9, c:0.59 },
  { m:'Jul', r:6.8, c:0.68 },
];
const maxR = Math.max(...monthlyData.map(d => d.r));

export const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);

  useEffect(() => {
    dashboardAPI.getStats().then(setStats);
    ordersAPI.getAll().then(setOrders);
  }, []);

  if (!stats) return (
    <div style={{ minHeight:'100vh', background:'var(--bg-base)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)', fontFamily:'DM Sans' }}>Loading...</div>
  );

  return (
    <div style={{ background:'var(--bg-base)', minHeight:'100vh', padding:'28px 32px' }} className="page-enter">
      <PageHeader title="Admin Dashboard" subtitle="Platform-wide performance overview"/>

      {/* Stats */}
      <Grid container spacing={2.5} sx={{ mb:3.5 }}>
        {[
          { title:'Total Revenue', value:`₹${(stats.totalRevenue/100000).toFixed(1)}L`, icon:<IndianRupee size={20}/>, change:'18% this month', positive:true, color:'var(--brand)' },
          { title:'Platform Commission', value:`₹${(stats.platformCommission/1000).toFixed(0)}K`, icon:<TrendingUp size={20}/>, change:'12% this month', positive:true, color:'#22c55e' },
          { title:'Total Orders', value:stats.totalOrders, icon:<Package size={20}/>, color:'#3b82f6' },
          { title:'Active Campaigns', value:stats.activeCampaigns, icon:<MapPin size={20}/>, color:'#8b5cf6' },
          { title:'Pending Approvals', value:stats.pendingApprovals, icon:<Clock size={20}/>, color:'#f59e0b' },
          { title:'Total Providers', value:stats.totalProviders, icon:<Users size={20}/>, color:'#06b6d4' },
        ].map((s,i) => (
          <Grid item xs={12} sm={6} lg={4} key={i}><StatCard {...s}/></Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        {/* Revenue Chart */}
        <Grid item xs={12} lg={7}>
          <Card>
            <SectionTitle>Monthly Revenue</SectionTitle>
            <div style={{ display:'flex', alignItems:'flex-end', gap:10, height:180 }}>
              {monthlyData.map((d,i) => (
                <div key={d.m} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
                  <span style={{ color:'var(--brand)', fontSize:10, fontWeight:600 }}>₹{d.r}L</span>
                  <div style={{ width:'100%', display:'flex', gap:2, alignItems:'flex-end' }}>
                    <div style={{ flex:1, background:'linear-gradient(to top, #e8621a, #fb923c)', borderRadius:'4px 4px 0 0', height:`${(d.r/maxR)*130}px`, transition:`height 0.6s ease ${i*0.07}s`, boxShadow:'0 2px 8px rgba(232,98,26,0.2)' }}/>
                    <div style={{ flex:1, background:'linear-gradient(to top, #22c55e, #4ade80)', borderRadius:'4px 4px 0 0', height:`${(d.c/maxR)*130}px`, transition:`height 0.6s ease ${i*0.07}s`, boxShadow:'0 2px 6px rgba(34,197,94,0.2)' }}/>
                  </div>
                  <span style={{ color:'var(--text-muted)', fontSize:11 }}>{d.m}</span>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:18, marginTop:14, justifyContent:'center' }}>
              {[{ color:'#e8621a', label:'Revenue' }, { color:'#22c55e', label:'Commission' }].map(l => (
                <div key={l.label} style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <div style={{ width:10, height:10, borderRadius:3, background:l.color }}/>
                  <span style={{ color:'var(--text-muted)', fontSize:12 }}>{l.label}</span>
                </div>
              ))}
            </div>
          </Card>
        </Grid>

        {/* Top Cities */}
        <Grid item xs={12} lg={5}>
          <Card>
            <SectionTitle>Top Cities by Revenue</SectionTitle>
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {topCities.map((c,i) => (
                <div key={c.city}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ width:22, height:22, borderRadius:6, background: i===0?'var(--brand-light)':'var(--bg-muted)', color: i===0?'var(--brand)':'var(--text-muted)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700 }}>{i+1}</div>
                      <div>
                        <div style={{ fontWeight:600, fontSize:13, color:'var(--text-primary)' }}>{c.city}</div>
                        <div style={{ fontSize:11, color:'var(--text-muted)' }}>{c.orders} orders</div>
                      </div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ color:'var(--brand)', fontWeight:700, fontSize:14 }}>₹{(c.revenue/100000).toFixed(1)}L</div>
                    </div>
                  </div>
                  <div style={{ height:5, background:'var(--bg-muted)', borderRadius:3, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${c.pct}%`, background:`linear-gradient(to right, #e8621a, #fb923c)`, borderRadius:3 }}/>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </Grid>

        {/* Recent Orders Table */}
        <Grid item xs={12}>
          <Card>
            <SectionTitle>Recent Orders</SectionTitle>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:700 }}>
                <thead>
                  <tr style={{ background:'var(--bg-muted)', borderRadius:10 }}>
                    {['Order ID','Company','Campaign','Amount','Payment','Status'].map(h => (
                      <th key={h} style={{ color:'var(--text-muted)', fontFamily:'DM Sans', fontSize:11, fontWeight:600, textAlign:'left', padding:'10px 14px', textTransform:'uppercase', letterSpacing:'0.06em', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o,i) => (
                    <tr key={o.id} style={{ borderBottom:'1px solid var(--border-light)' }}>
                      <td style={{ padding:'13px 14px', color:'var(--text-muted)', fontSize:12, fontFamily:'DM Sans', whiteSpace:'nowrap' }}>{o.id}</td>
                      <td style={{ padding:'13px 14px', fontWeight:600, fontSize:13, color:'var(--text-primary)' }}>{o.companyName}</td>
                      <td style={{ padding:'13px 14px', fontSize:12, color:'var(--text-secondary)', maxWidth:160 }}><div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{o.campaignName}</div></td>
                      <td style={{ padding:'13px 14px', color:'var(--brand)', fontWeight:700, fontSize:13, whiteSpace:'nowrap' }}>₹{o.grandTotal.toLocaleString('en-IN')}</td>
                      <td style={{ padding:'13px 14px' }}>
                        <span style={{ background:o.paymentStatus==='Received'?'var(--success-bg)':'var(--warning-bg)', color:o.paymentStatus==='Received'?'var(--success)':'var(--warning)', padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:600, fontFamily:'DM Sans' }}>{o.paymentStatus}</span>
                      </td>
                      <td style={{ padding:'13px 14px' }}><StatusBadge status={o.status} size="sm"/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};
